import { ObjectUrlCache, cacheBlob } from '../gallery/objectUrlCache';
import { mapWithConcurrency, probeImage } from './probeImage';
import { readExifDate } from './exifDate';
import type { PhotoItem, PhotoSource, PhotoSourceProgress } from './types';

/**
 * Photos from the user's own machine. No auth, no network, no Google.
 *
 * This is the source the gallery is developed against (PLAN.md §5 Phase 2) — it makes every
 * creative iteration a sub-second loop instead of an OAuth round trip, and it is immune to
 * Google deprecating things.
 */

const SUPPORTED = /^image\/(jpeg|png|webp|gif|avif)$/;

/** Probing decodes at full size; 4 concurrent keeps peak memory sane on mobile. */
const PROBE_CONCURRENCY = 4;

/** Local object URLs are cheap handles, so this can be generous. */
const URL_CACHE_SIZE = 600;

export function isSupportedImage(file: File): boolean {
  return SUPPORTED.test(file.type);
}

export class LocalFilesSource implements PhotoSource {
  readonly id = 'local' as const;
  readonly label = 'Local files';
  readonly ephemeral = false;

  private items: PhotoItem[] = [];
  private cache = new ObjectUrlCache(URL_CACHE_SIZE);

  constructor(private readonly files: File[]) {}

  async connect(signal: AbortSignal, onProgress?: (p: PhotoSourceProgress) => void): Promise<void> {
    const images = this.files.filter(isSupportedImage);
    if (images.length === 0) {
      throw new Error('No supported images found. Try JPEG, PNG, WebP, GIF, or AVIF.');
    }

    onProgress?.({ loaded: 0, total: images.length, label: 'Reading photos' });

    const probes = await mapWithConcurrency(
      images,
      PROBE_CONCURRENCY,
      async (file) => ({
        ...(await probeImage(file)),
        // Real capture time from the JPEG header. `File.lastModified` is deliberately not used
        // as a fallback: it is usually when the file was copied, and a wrong chapter heading is
        // worse than no chapter at all (Q10).
        capturedAt: await readExifDate(file),
      }),
      (loaded) => onProgress?.({ loaded, total: images.length, label: 'Reading photos' }),
      signal
    );

    this.items = images.map((file, i) => {
      const probe = probes[i];
      const id = `local:${i}:${file.name}:${file.size}`;
      return {
        id,
        width: probe.width,
        height: probe.height,
        mimeType: file.type,
        createdAt: probe.capturedAt ?? undefined,
        interest: probe.interest,
        placeholder: probe.placeholder,
        // The requested size is ignored here: the browser scales a local object URL for free.
        // The Google source will use it to pick a `=w{n}-h{n}` variant.
        resolve: async () => cacheBlob(this.cache, id, file),
        peek: () => this.cache.get(id),
        release: () => this.cache.delete(id),
      };
    });
  }

  list(): PhotoItem[] {
    return this.items;
  }

  dispose(): void {
    this.cache.clear();
    this.items = [];
  }
}
